// Modo Sandbox → PostHog.
//
// El Sandbox es un estado de la compañía (se enciende en Perfil → Ajustes o en
// el menú de cuenta) que llena la consola de datos de ejemplo. Lo que alguien
// hace con datos de ejemplo no es uso real, así que cada evento dice en qué
// modo se hizo (`sandbox_mode`), la ficha de la persona en PostHog dice su
// estado actual, y encenderlo o apagarlo deja su propio evento.
//
// La fuente es el store que ya consulta la API (`features/sandbox`); aquí sólo
// se escucha. Nada de esto puede tumbar la app: las primitivas de PostHog no
// lanzan y el almacenamiento va envuelto.

import { useSandboxStore } from "../features/sandbox/sandboxStore";
import { capturePostHogEvent, setPostHogSandboxMode } from "../posthog";

// Último estado conocido de ESTA sesión (por token): al recargar, los primeros
// eventos —la página de entrada— salen antes de que la API responda, y sin esto
// se marcarían como «no Sandbox» aunque el modo esté encendido.
const STORAGE_KEY = "analytics:sandbox_mode";

interface Remembered {
  token: string;
  enabled: boolean;
}

const readRemembered = (): Remembered | null => {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const value = JSON.parse(raw) as Partial<Remembered>;
    return typeof value.token === "string" && typeof value.enabled === "boolean"
      ? { token: value.token, enabled: value.enabled }
      : null;
  } catch {
    return null;
  }
};

const remember = (value: Remembered | null): void => {
  try {
    if (value) sessionStorage.setItem(STORAGE_KEY, JSON.stringify(value));
    else sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Sin almacenamiento sólo se pierde la marca de los primeros eventos.
  }
};

const currentToken = (): string => {
  try {
    return localStorage.getItem("token") || "";
  } catch {
    return "";
  }
};

let started = false;

/**
 * Empieza a seguir el Modo Sandbox. Se llama una vez al arrancar, después de
 * `initPostHog`. Devuelve la función para dejar de escuchar (pruebas).
 */
export function startSandboxAnalyticsSync(): () => void {
  if (started) return () => undefined;
  started = true;

  // 1. Lo que se sabía de esta misma sesión, para los eventos del arranque.
  const remembered = readRemembered();
  let known: Remembered | null = remembered && remembered.token === currentToken() ? remembered : null;
  if (known) setPostHogSandboxMode(known.enabled);
  else if (remembered) remember(null);

  // 2. Lo que diga la API de aquí en adelante.
  const apply = (state: { enabled: boolean; loadedFor: string | null; resolvedFor: string | null }) => {
    // Sin sesión no se sabe nada (y `resetPostHog` ya lo dejó así).
    if (!state.loadedFor) {
      if (known) {
        known = null;
        remember(null);
        setPostHogSandboxMode(null);
      }
      return;
    }
    // La petición va en camino (o falló): `enabled` aún es el valor por omisión
    // del store, no el estado real. Se conserva lo que ya se sabía.
    if (state.resolvedFor !== state.loadedFor) return;

    const sameSession = known !== null && known.token === state.resolvedFor;
    if (sameSession && known!.enabled === state.enabled) return;

    const changed = sameSession && known!.enabled !== state.enabled;
    known = { token: state.resolvedFor, enabled: state.enabled };
    remember(known);
    // Primero el modo, luego el evento: el evento del cambio ya sale marcado
    // con el modo NUEVO.
    setPostHogSandboxMode(state.enabled);
    if (changed) capturePostHogEvent("sandbox_mode_changed", { enabled: state.enabled });
  };

  const unsubscribe = useSandboxStore.subscribe(apply);
  apply(useSandboxStore.getState());

  return () => {
    unsubscribe();
    started = false;
  };
}
